/**
 * Detecção de ambiente e limpeza idempotente de Service Worker / Cache Storage
 * focada em destravar o preview do Lovable sem interferir em produção.
 */

// Refresh build comment: 2026-06-03 - Build ID Display v1

export const isInIframe = (() => {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    return true;
  }
})();

export function getEnvironment(): 'production' | 'preview' | 'development' {
  if (typeof window === 'undefined') return 'production';
  const host = window.location.hostname;

  // Produção (domínios publicados / customizados)
  if (
    host === 'autofluxo.app' ||
    host === 'www.autofluxo.app' ||
    host === 'autofluxo1.lovable.app'
  ) {
    return 'production';
  }

  // Preview / editor do Lovable
  if (
    host.includes('id-preview--') ||
    host.startsWith('preview--') ||
    host.endsWith('.lovableproject.com') ||
    host.endsWith('.lovable.dev')
  ) {
    return 'preview';
  }

  if (host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.')) {
    return 'development';
  }

  // Fallback: qualquer outro *.lovable.app tratamos como preview pra não cachear
  if (host.endsWith('.lovable.app')) return 'preview';

  return 'production';
}

export const BUILD_MARKER = `autofluxo-build-${import.meta.env.MODE}-${
  (import.meta as any).env?.VITE_BUILD_VERSION || 'dev'
}`;

const CLEANUP_FLAG_KEY = 'autofluxo_preview_cleanup_session';
const RUNTIME_RECOVERY_KEY = 'autofluxo_preview_runtime_recovery';

/**
 * Reconhece erros provocados por módulos antigos da aba (HMR/camada de
 * dependências desatualizada), que não se resolvem sozinhos: a aba segue
 * renderizando um componente que aponta para um vínculo que já saiu do código
 * ("X is not defined") ou para um módulo que não existe mais.
 */
export function isStaleModuleError(message: string, stack: string, hasErrorObject: boolean): boolean {
  const text = `${message || ''}\n${stack || ''}`;

  const isStaleReactRuntime =
    /Cannot read properties of null \(reading 'use[A-Z]/.test(message) &&
    (stack.includes('/node_modules/.vite/deps/') || stack.includes('renderWithHooks'));
  if (isStaleReactRuntime) return true;

  const looksLikeAppModule =
    stack.includes('/src/') || stack.includes('/node_modules/.vite/') || stack.includes('renderWithHooks');
  if (!looksLikeAppModule) return false;

  if (hasErrorObject && /is not defined/.test(text)) return true;

  return /Failed to (fetch dynamically imported module|resolve dynamic import)|Cannot find module|does not provide an export named/.test(
    text,
  );
}

/**
 * Uma aba de preview aberta durante uma nova otimização de dependências pode
 * manter o renderer antigo e receber hooks do React novo via HMR. Nesse caso,
 * faz uma única recarga completa para reconstruir o grafo de módulos.
 */
export function installPreviewRuntimeRecovery(): void {
  if (typeof window === 'undefined' || getEnvironment() === 'production') return;

  window.addEventListener('error', (event) => {
    const message = event.error instanceof Error ? event.error.message : event.message;
    const stack = event.error instanceof Error ? event.error.stack || '' : '';

    if (!isStaleModuleError(message, stack, event.error instanceof Error)) return;

    try {
      if (sessionStorage.getItem(RUNTIME_RECOVERY_KEY) === BUILD_MARKER) return;
      sessionStorage.setItem(RUNTIME_RECOVERY_KEY, BUILD_MARKER);
    } catch {
      return;
    }

    window.location.reload();
  });
}

/**
 * Em preview/dev: desregistra qualquer SW, limpa Cache Storage e marca a sessão
 * para não rodar de novo (evita loops). Em produção: no-op.
 *
 * Importante: NÃO mexe em localStorage de autenticação.
 */
export async function runPreviewCacheGuard(): Promise<void> {
  if (typeof window === 'undefined') return;
  const env = getEnvironment();

  // Marcador técnico de build (sem alterar UI)
  try {
    document.documentElement.dataset.buildEnv = env;
    document.documentElement.dataset.buildMarker = BUILD_MARKER;
    
    // Log visual no DevTools com banner
    // eslint-disable-next-line no-console
    console.info(
      `%c [AutoFluxo] %c ${BUILD_MARKER} %c ${env} `,
      'background: #0080ff; color: #fff; font-weight: bold; border-radius: 3px 0 0 3px;',
      'background: #333; color: #fff; font-weight: bold;',
      `background: ${env === 'production' ? '#22c55e' : '#f59e0b'}; color: #fff; font-weight: bold; border-radius: 0 3px 3px 0;`
    );
  } catch {/* ignore */}

  if (env === 'production') return;

  // Evita rodar mais de uma vez na mesma sessão (previne loop)
  try {
    if (sessionStorage.getItem(CLEANUP_FLAG_KEY) === BUILD_MARKER) return;
    sessionStorage.setItem(CLEANUP_FLAG_KEY, BUILD_MARKER);
  } catch {/* ignore */}

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch {/* ignore */}

  try {
    if (typeof caches !== 'undefined') {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n).catch(() => false)));
    }
  } catch {/* ignore */}
}
